import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  AreaChart, Area, BarChart, Bar as RBar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import api from "@/lib/api";
import { GlassCard, Kpi, Skeleton, Table, tip, PageHead } from "@/v2/admin/ui";
import { useDept } from "./DeptShell";
import { BarChart3, Users, Activity, Gauge, Trophy, PlayCircle, TrendingUp } from "lucide-react";

const dateBefore = (d) => new Date(Date.now() - d * 864e5).toISOString().slice(0, 10);

export default function DeptAnalytics() {
  const { q, meta } = useDept();
  const [from, setFrom] = useState(dateBefore(30));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));
  const [section, setSection] = useState("");

  const { data: s, isLoading } = useQuery({
    queryKey: ["dept-an", q, from, to, section],
    queryFn: async () => (await api.get(`/dept/analytics?${q}&date_from=${from}&date_to=${to}&section=${encodeURIComponent(section)}`)).data,
  });

  return (
    <div className="space-y-5" data-testid="dept-analytics">
      <PageHead icon={BarChart3} title="تحليلات الإدارة" sub="تحليل أداء موظفي إدارتك خلال أي فترة زمنية." />

      <div className="flex flex-wrap gap-3">
        <div><label className="v2-label">من</label><input type="date" dir="ltr" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="dept-an-from" className="v2-input !w-auto" /></div>
        <div><label className="v2-label">إلى</label><input type="date" dir="ltr" value={to} onChange={(e) => setTo(e.target.value)} data-testid="dept-an-to" className="v2-input !w-auto" /></div>
        <div><label className="v2-label">القسم</label>
          <select value={section} onChange={(e) => setSection(e.target.value)} data-testid="dept-an-section" className="v2-input !w-auto text-sm font-bold">
            <option value="">كل الأقسام</option>
            {(meta?.sections || []).map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
        </div>
      </div>

      {isLoading || !s ? <Skeleton className="h-96 w-full" /> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4">
            <Kpi icon={Users} label="موظفو النطاق" value={s.employees} testId="dan-employees" />
            <Kpi icon={TrendingUp} label="نسبة المشاركة" value={s.participation_rate} suffix="%" tone="gold" />
            <Kpi icon={Activity} label="محاولات الاختبار" value={s.attempts_count} />
            <Kpi icon={Trophy} label="نسبة النجاح" value={s.pass_rate} suffix="%" />
            <Kpi icon={Gauge} label="متوسط الدرجات" value={s.avg_score} suffix="%" />
            <Kpi icon={PlayCircle} label="متوسط المشاهدة" value={s.avg_watch_pct} suffix="%" tone="gold" />
          </div>

          <GlassCard className="p-5">
            <h3 className="font-bold mb-1">الحضور والمحاولات خلال الفترة</h3>
            {s.series_capped && (
              <p className="text-[11px] text-[#e3c799] mb-3" data-testid="dept-an-capped">
                ملاحظة: الرسم البياني يعرض أول 60 يومًا من الفترة المختارة — أمّا المؤشرات أعلاه فتشمل الفترة كاملة.
              </p>
            )}
            <div className="mb-3" />
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={s.series}>
                <defs>
                  <linearGradient id="dan1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#91cab8" stopOpacity={0.5} /><stop offset="100%" stopColor="#91cab8" stopOpacity={0} /></linearGradient>
                  <linearGradient id="dan2" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#bb9661" stopOpacity={0.4} /><stop offset="100%" stopColor="#bb9661" stopOpacity={0} /></linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="day" tick={{ fill: "#bcc8cf", fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fill: "#bcc8cf", fontSize: 11 }} />
                <Tooltip {...tip} />
                <Area name="حضور" dataKey="checkins" stroke="#91cab8" strokeWidth={2} fill="url(#dan1)" />
                <Area name="محاولات" dataKey="attempts" stroke="#bb9661" strokeWidth={2} fill="url(#dan2)" />
              </AreaChart>
            </ResponsiveContainer>
          </GlassCard>

          {(s.challenges || []).length > 0 && (
            <>
              <GlassCard className="p-5">
                <h3 className="font-bold mb-4">متوسط الدرجات لكل تحدٍ</h3>
                <ResponsiveContainer width="100%" height={250}>
                  <BarChart data={s.challenges}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <XAxis dataKey="day" tick={{ fill: "#bcc8cf", fontSize: 11 }} />
                    <YAxis tick={{ fill: "#bcc8cf", fontSize: 11 }} />
                    <Tooltip {...tip} />
                    <RBar name="متوسط الدرجة" dataKey="avg_score" fill="#bb9661" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </GlassCard>

              <Table head={["اليوم", "التحدي", "المحاولات", "نسبة النجاح", "متوسط الدرجة"]} min={620} testId="dept-an-table">
                {s.challenges.map((c) => (
                  <tr key={c.id || c.day}>
                    <td className="font-bold v2-gold-text">{c.day}</td>
                    <td>{c.title}</td>
                    <td>{c.attempts}</td>
                    <td>{c.pass_rate}%</td>
                    <td>{c.avg_score}%</td>
                  </tr>
                ))}
              </Table>
            </>
          )}
          {(s.challenges || []).length === 0 && (
            <GlassCard className="p-10 text-center text-[#bcc8cf] text-sm">لا توجد محاولات اختبار خلال هذه الفترة.</GlassCard>
          )}
        </>
      )}
    </div>
  );
}
